import { FadeLoader} from "react-spinners";
import {useTranslation} from "react-i18next";
import {useEffect, useState} from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Avatar,
} from '@mui/material';
// components
import { ConfirmationDialog } from "../../components/general/ConfirmationDialog";
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import Iconify from '../../components/Iconify';
import SearchNotFound from '../../components/SearchNotFound';
import useQuery from "../../hooks/useQuery";
import { ListHead, ListToolbar, MoreMenu } from '../../components/tables';
import { useApi } from "../../hooks/useApi";

const override = {  
  margin: "0 auto",
};

export default function LoanIndex() {
  const [loans ,setLoans] = useState([]);
  const [selectedLoan ,setSelectedLoan] = useState();
  const [pagination, setPagination] = useState({total: 0, per_page: 0, current_page: 1, last_page: 1});
  const [page, setPage] = useState(1);
  const [filter ,setFilter] = useState("");
  const [sort ,setSort] = useState('asc');
  const [sortField ,setSortField] = useState('employee_id');
  const [selected, setSelected] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const {doDelete} = useApi();
  const [loading, setLoading] = useState(true);
  
  const fetchData = useQuery();
  const { t } = useTranslation();

  const TABLE_HEAD = [
    { id: 'employee_id', label: t('employee'), alignRight: false },
    { id: 'total_amount', label: t('total_amount'), alignRight: false },
    { id: 'amount_paid', label: t('amount_paid'), alignRight: false },
    { id: 'paid_amount', label: t('payments_made'), alignRight: false, sortable: false },
    { id: 'remaining_payments', label: t('remaining_payments'), alignRight: false, sortable:false},
    { id: 'paid', label: t('status'), alignRight: false },
    { id: 'type', label: t('type'), alignRight: false },
    { id: 'id' },
  ];

  const refreshTable = async () => {
    setIsLoading(true)
    await fetchData('loans', filter, sort, setLoans, sortField, setPagination, page, setNotFound)
    setIsLoading(false)
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(refreshTable, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [filter, sort, page, sortField])


  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = loans.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleDeleteDialog = (loan) => {
    setSelectedLoan(loan)
    setShowDeleteDialog(true)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async () =>{
    setLoading(true)
    doDelete(`loans/${selectedLoan.id}`, t('delete_success'), t('delete_error'), refreshTable);
    setLoading(false); 
    setShowDeleteDialog(false);
    setSelected([]);
  }

  const handleMassDelete = async () => {
    setLoading(true);
    doDelete(`loans/mass`, t('delete_mass_success'), t('delete_mass_error'), refreshTable, {ids: selected}, true); 
    setLoading(false);
    setSelected([]);
  }

  const handleFilterByName = (event) => {
    setFilter(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - loans.length) : 0;

  return (
    <Page title={t('Loans')}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {t('Loans')}
          </Typography>
          <Button variant="contained" component={RouterLink} to="/dashboard/loans/save" startIcon={<Iconify icon="eva:plus-fill" />}>
            {t('new_loan')}
          </Button>
        </Stack>

        <Card>
          <ListToolbar numSelected={selected.length} filterName={filter} onFilterName={handleFilterByName} placeholder={`search_loans`} handleMassDelete={handleMassDelete} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                {isLoading?  <FadeLoader color={'blue'} loading={loading} cssOverride={override} size={50} /> :
                <>
                <ListHead
                  headLabel={TABLE_HEAD}
                  rowCount={loans.length}
                  order={sort}
                  orderBy={sortField}
                  omitCheckbox
                  // numSelected={selected.length}
                  // onRequestSort={handleRequestSort}
                  onRequestSort={setSortField}
                  setSort={setSort}
                 

                />
                <TableBody>
                   {loans.map((row) => {
                      const {employee, remaining_payments: remainingPayments, payments_made: paymentsMade,employee_id:employeeId, id, total_amount:totalAmount, paid_amount:paidAmount,fortnight_qty:fortnightQty,paid,type} = row;
                      const isItemSelected = selected.indexOf(id) !== -1;
                      return (
                        <TableRow
                          hover
                          key={id}
                          tabIndex={-1}
                          city="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                        
                          <TableCell component="th" scope="row" padding="none" sx={{ pl: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle2" noWrap>
                                {employee.fullname}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="left">{t(totalAmount)}</TableCell>
                          <TableCell align="left">{paidAmount}</TableCell>
                          <TableCell align="left">{paymentsMade}</TableCell>
                          <TableCell align="left">{remainingPayments}</TableCell>
                          <TableCell align="left">{paid?"Pagado":"Pendiente"}</TableCell>
                        
                          <TableCell align="left">{t(type)}</TableCell>
                          <TableCell align="right">
                            <MoreMenu url={`/dashboard/loans/edit/${id}`} onDelete={() => handleDeleteDialog(row) }/>
                          </TableCell>
                        </TableRow>
                      );
                    }) }
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {notFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filter} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
                </>}
                  </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page}
            page={page -1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <ConfirmationDialog
            open={showDeleteDialog}
            onClose={() => {setShowDeleteDialog(false)}}
            onConfirm={handleDelete}
            message={t('delete_confirm')}
           />
        </Card>
      </Container>
    </Page>
  );
}
